/**
 * Un singur drum catre backend-ul nou (/api/v2). Cookie-ul `od_v4` pleaca
 * singur (acelasi domeniu); un 401 inseamna ca omul nu mai are sesiune, si il
 * trimitem la intrarea LUI v4, cu drumul de intoarcere.
 */
import { announceIdle, beginSignOut, idleMinutesOf, isSigningOut } from './idle'
import { refreshSession } from './session'

export class ApiError extends Error {
  readonly status: number

  /**
   * Corpul JSON al raspunsului de eroare, cand a fost unul.
   *
   * `message` ramane singurul lucru pe care se sprijina toata lumea; asta e
   * pentru capetele care au de spus mai mult decat o propozitie - formularul de
   * cont nou intoarce TOATE motivele deodata (`messages`), ca omul sa nu afle a
   * doua jumatate a regulii de parola abia la a doua incercare.
   */
  readonly details?: Record<string, unknown>

  constructor(status: number, message: string, details?: Record<string, unknown>) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.details = details
  }
}

/** Lista de motive dintr-o eroare de API, cand capatul a trimis una. */
export function errorMessages(error: unknown): string[] {
  if (!(error instanceof ApiError)) return []
  const list = error.details?.messages
  if (!Array.isArray(list)) return []
  return list.map((item) => String(item)).filter((item) => item !== '')
}

/**
 * Ecranul de intrare AL LUI v4, nu `/login` al portalului vechi.
 *
 * v4 va rula la ALTA ADRESA, unde portalul vechi nici nu exista: `/login` de
 * acolo ar duce intr-un 404, iar cookie-ul e al domeniului - de la o adresa
 * straina nimeni nu s-ar intoarce logat. Aplicatia sta sub baza `/app/` (vezi
 * `base` din vite.config.ts), iar intrarea e un DOCUMENT separat, `login.html`
 * (vezi `rolldownOptions.input`), nu o ruta din router - deci se cere cu tot cu
 * numele fisierului.
 */
export const LOGIN_URL = '/app/login.html'

export function sendToLogin(): void {
  /* Cine pleaca de bunavoie nu se intoarce „unde era": la intrare, si atat. */
  if (isSigningOut()) {
    window.location.assign(LOGIN_URL)
    return
  }
  const back = encodeURIComponent(window.location.pathname + window.location.search)
  window.location.assign(`${LOGIN_URL}?forward=${back}`)
}

export const LOGOUT_URL = '/api/v2/auth/logout'

/**
 * Iesirea voita: `User::sign_out` din portal, pe capatul lui v4.
 *
 * Intai se spune barei de inactivitate ca omul PLEACA (`beginSignOut`), ca
 * niciun 401 din cererile inca pe drum - cu sau fara `reason: 'idle'` - sa nu
 * o mai aprinda; abia apoi cererea, care sterge cookie-ul si inchide randul din
 * `it_user_sessions`; si la urma documentul de intrare, fara `forward`.
 *
 * `finally`: si daca cererea pica, omul trebuie sa ajunga la ecranul de
 * intrare. Un buton de iesire care te lasa pe loc e mai rau decat unul lipsa.
 */
export function signOut(go: (url: string) => void = (url) => window.location.assign(url)): Promise<void> {
  beginSignOut()

  return fetch(LOGOUT_URL, { method: 'POST', credentials: 'include' })
    .then(
      () => undefined,
      () => undefined
    )
    .finally(() => go(LOGIN_URL))
}

/**
 * PLASA DE LA 401, NU REINNOIREA INSASI.
 *
 * La primul 401 se cere o data un token nou. Daca backendul il da, cererea se
 * face din nou si omul nu simte nimic. Daca nu-l da - sesiunea chiar a picat -
 * atunci il trimitem la intrare, ca inainte.
 *
 * O SINGURA data pe cerere. Un 401 care se intoarce dupa un token proaspat nu e
 * o sesiune expirata, ci un drept care lipseste; reincercat la nesfarsit ar
 * face din el o bucla care bate serverul si nu spune niciodata ce e.
 *
 * Plasa asta NU poate tine singura sesiunea, si multa vreme s-a crezut ca
 * poate: ea se intinde abia DUPA ce a venit un 401, iar `/api/v2/auth/refresh`
 * cere un jeton VIU. Cand cookie-ul e mort de un minut, reinnoirea raspunde tot
 * 401 si omul pleaca la ecranul de intrare - exact cazul pentru care fusese
 * scrisa. Ce mai muta ceasul inainte sa moara sta in `session.ts`, si se uita
 * la ce face OMUL, nu la ce raspunde serverul.
 */
export { SESSION_REFRESH_URL, refreshSession } from './session'

/**
 * INACTIV NU E DELOGAT.
 *
 * Un 401 cu `reason: 'idle'` vine de la un jeton expirat de nelucru, dar
 * inca al omului (orele furnizorului n-au trecut). Nu se reincearca - un
 * jeton nou nu se da fara cod - si nu se pleaca la intrare: se aprinde bara
 * din josul paginii (`ui/IdleBar.tsx`), care cere codul pe mail si reincarca
 * pagina cand omul s-a intors. Pagina ramane, cu tot ce era pe ea.
 */
async function idleStop(response: Response): Promise<boolean> {
  const minutes = await idleMinutesOf(response)
  if (minutes === null) return false
  announceIdle(minutes)
  return true
}

export async function apiGet<T>(path: string, init?: RequestInit): Promise<T> {
  return apiCall<T>(path, init)
}

/** POST multipart, cum trimitea ajaxForm cu fisiere. */
export async function apiPostForm<T>(path: string, body: FormData): Promise<T> {
  return apiCall<T>(path, { method: 'POST', body })
}

export type PostScalar = string | number | boolean
export type PostValue = PostScalar | PostScalar[] | Array<Record<string, PostScalar>>

/** POST cu campuri de formular (application/x-www-form-urlencoded), cum trimitea ajaxSubmit. */
export async function apiPost<T>(path: string, fields: Record<string, PostValue>): Promise<T> {
  return apiCall<T>(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: encodePost(fields),
  })
}

/**
 * POST cu corp JSON. Pentru ce NU incape in campuri de formular: constructorul de
 * formulare trimite un arbore (campuri cu optiunile lor), nu perechi cheie-valoare.
 */
export async function apiPostJson<T>(path: string, payload: unknown): Promise<T> {
  return apiCall<T>(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
}

/** Form-urlencoded identic cu ce citea CI3 din `devices[]` si `devices[i][deviceId]`. */
export function encodePost(fields: Record<string, PostValue>): URLSearchParams {
  const body = new URLSearchParams()
  for (const [key, value] of Object.entries(fields)) appendPost(body, key, value)
  return body
}

function appendPost(body: URLSearchParams, key: string, value: PostValue): void {
  if (Array.isArray(value)) {
    value.forEach((item, i) => {
      if (item !== null && typeof item === 'object') {
        for (const [inner, innerVal] of Object.entries(item)) {
          body.append(`${key}[${i}][${inner}]`, String(innerVal))
        }
        return
      }
      body.append(key.endsWith('[]') ? key : `${key}[]`, String(item))
    })
    return
  }
  body.append(key, String(value))
}

/**
 * POST care intoarce corpul brut. QR / CSV / addDevice nu raspund tot
 * timpul JSON: HTML-ul formularului, PDF-ul, textul gol de succes, sau
 * "Field x is required" pe 401 ca in CI3 (nu e sesiune picata).
 */
export async function apiExchange(path: string, init?: RequestInit): Promise<{ status: number; text: string; type: string; bytes: ArrayBuffer; filename: string }> {
  const send = (): Promise<Response> => fetch(`/api/v2${path}`, {
    ...init,
    credentials: 'same-origin',
  })

  let response = await send()

  /* `Field x is required` vine cu 401 din CI3 si NU e sesiune picata: e
     raspunsul unui formular caruia ii lipseste un camp. Nu se reincearca. */
  if (response.status === 401) {
    const first = new TextDecoder().decode(await response.clone().arrayBuffer())

    if (await idleStop(response)) throw new ApiError(401, 'Signed out for inactivity')

    if (!first.startsWith('Field ') && (await refreshSession())) {
      response = await send()
    }
  }

  const bytes = await response.arrayBuffer()
  const type = response.headers.get('Content-Type') ?? ''
  const disposition = response.headers.get('Content-Disposition') ?? ''
  const file = /filename="([^"]+)"/.exec(disposition)
  const text = new TextDecoder().decode(bytes)
  if (response.status === 401 && !text.startsWith('Field ')) {
    sendToLogin()
    throw new ApiError(401, 'Not logged in')
  }
  if (!response.ok) {
    let message = `${response.status} ${response.statusText}`
    try {
      const body = JSON.parse(text) as { message?: string }
      if (body.message) message = body.message
    } catch {
      if (text.trim() !== '') message = text.trim()
    }
    throw new ApiError(response.status, message)
  }
  return { status: response.status, text, type, bytes, filename: file?.[1] ?? '' }
}

async function apiCall<T>(path: string, init?: RequestInit): Promise<T> {
  const send = (): Promise<Response> => fetch(`/api/v2${path}`, {
    ...init,
    credentials: 'same-origin',
    headers: { Accept: 'application/json', ...(init?.headers ?? {}) },
  })

  let response = await send()

  if (response.status === 401 && (await idleStop(response))) {
    throw new ApiError(401, 'Signed out for inactivity')
  }

  if (response.status === 401 && (await refreshSession())) {
    response = await send()
  }

  if (response.status === 401) {
    sendToLogin()
    throw new ApiError(401, 'Not logged in')
  }

  if (!response.ok) {
    let message = `${response.status} ${response.statusText}`
    let details: Record<string, unknown> | undefined
    try {
      const body = (await response.json()) as { message?: string }
      if (body.message) message = body.message
      details = body as Record<string, unknown>
    } catch {
      /* corpul nu e JSON; ramane statusul */
    }
    throw new ApiError(response.status, message, details)
  }

  return (await response.json()) as T
}
